import { executeGet } from '@/services/lukita'

export const FIRST_PAGE_LIMIT = 200
export const NEXT_PAGE_LIMIT = 1000
// A page that has not arrived by now is treated as failed, so a stalled request cannot keep the
// list loading forever.
export const PAGE_TIMEOUT_MS = 60_000

interface PageStatement {
  id: number
  file_name: string
  file_url: string
}

interface PagedRow {
  statement_id: number
}

interface Page<Row> {
  rows: Row[]
  hasMore: boolean
}

// progressive shows each page as it lands; whole swaps the list in once every page is in.
export type LoadMode = 'progressive' | 'whole'

// Where a page walk starts: page 1 has no cursor.
export interface PageStart {
  page: number
  after: string | null
}

export const FIRST_PAGE: PageStart = { page: 1, after: null }

// The page that failed, and where a retry starts so no row is lost.
export interface PageFailure {
  page: number
  resumeFrom: PageStart
}

export class PageTimeoutError extends Error {
  constructor(page: PageStart) {
    super(`Page ${page.page} did not arrive within ${PAGE_TIMEOUT_MS} ms.`)
    this.name = 'PageTimeoutError'
  }
}

export class MalformedPageError extends Error {
  constructor(page: PageStart, problem: string) {
    super(`Page ${page.page} ${problem}.`)
    this.name = 'MalformedPageError'
  }
}

// Paged rows leave out their statement's file name and link; the page carries its statements instead.
const withStatementFiles = <Row extends PagedRow>(
  rows: Row[],
  statements: PageStatement[],
): Row[] => {
  const statementsById = new Map(
    statements.map((statement) => [statement.id, statement]),
  )
  return rows.map((row) => {
    const statement = statementsById.get(row.statement_id)
    if (!statement) return row
    return {
      ...row,
      statement_file_name: statement.file_name,
      statement_file_url: statement.file_url,
    }
  })
}

interface LoadPagesOptions<Row> {
  path: string
  root: string
  mode: LoadMode
  isStale: () => boolean
  store: (rows: Row[], arrival: { isFirst: boolean }) => void
  onProgress: (progress: { hasMore: boolean }) => void
  // Called once, before the error is rethrown, when a page of a current walk fails.
  onPageFailed?: (failure: PageFailure) => void
  start?: PageStart
}

type FetchedPage<Row> = Page<Row> & { next: string | null }

// A page must carry its rows. A page with no rows that still points to more, or that points
// back to itself, would loop forever.
const parsePage = <Row extends PagedRow>(
  body: unknown,
  root: string,
  start: PageStart,
): FetchedPage<Row> => {
  // An API without paging ignores limit and sends every row at once.
  if (Array.isArray(body)) return { rows: body, hasMore: false, next: null }

  const envelope = (body ?? {}) as Record<string, unknown>
  const rows = envelope[root]
  if (!Array.isArray(rows)) {
    throw new MalformedPageError(start, `has no "${root}" list`)
  }
  const next = (envelope.next_cursor as string | null | undefined) ?? null
  if (next !== null && rows.length === 0) {
    throw new MalformedPageError(start, 'is empty but says more rows follow')
  }
  if (next !== null && next === start.after) {
    throw new MalformedPageError(start, 'points back to itself')
  }
  const statements = (envelope.statements as PageStatement[] | undefined) ?? []
  return {
    rows: withStatementFiles(rows as Row[], statements),
    hasMore: next !== null,
    next,
  }
}

// lukita's client swallows an aborted request and resolves with nothing, so a missing response
// after the timer fired is the timeout.
const fetchPage = async <Row extends PagedRow>(
  path: string,
  root: string,
  start: PageStart,
): Promise<FetchedPage<Row>> => {
  const limit = start.after ? NEXT_PAGE_LIMIT : FIRST_PAGE_LIMIT
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PAGE_TIMEOUT_MS)
  try {
    const response = await executeGet(path, {
      queryParams: start.after ? { limit, after: start.after } : { limit },
      aborter: { abortController: () => controller },
    })
    if (!response) throw new PageTimeoutError(start)
    return parsePage<Row>(await response.json(), root, start)
  } catch (error) {
    if (controller.signal.aborted) throw new PageTimeoutError(start)
    throw error
  } finally {
    clearTimeout(timer)
  }
}

// The first page is small so the ledger paints fast; the rest arrive in bigger pages behind it.
async function* fetchPages<Row extends PagedRow>(
  path: string,
  root: string,
  start: PageStart,
): AsyncGenerator<FetchedPage<Row>> {
  let current: PageStart = start
  do {
    const fetched = await fetchPage<Row>(path, root, current)
    yield fetched
    current = { page: current.page + 1, after: fetched.next }
  } while (current.after)
}

export const loadPages = async <Row extends PagedRow>({
  path,
  root,
  mode,
  isStale,
  store,
  onProgress,
  onPageFailed,
  start = FIRST_PAGE,
}: LoadPagesOptions<Row>): Promise<void> => {
  const buffered: Row[] = []
  let isFirst = start.after === null
  let pending = start

  try {
    for await (const { rows, hasMore, next } of fetchPages<Row>(
      path,
      root,
      start,
    )) {
      if (isStale()) return

      if (mode === 'whole') {
        buffered.push(...rows)
        if (!hasMore) store(buffered, { isFirst: true })
      } else {
        store(rows, { isFirst })
        onProgress({ hasMore })
        isFirst = false
      }
      pending = { page: pending.page + 1, after: next }
    }
  } catch (error) {
    // Whole mode drops the pages it buffered, so its retry starts over.
    if (!isStale()) {
      onPageFailed?.({
        page: pending.page,
        resumeFrom: mode === 'whole' ? start : pending,
      })
    }
    throw error
  }
}
